import { useState } from "react";
import {
  useQuery,
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import { PlusIcon } from "@heroicons/react/24/outline";
import { fetchStoreUsers } from "../api/queries";
import {
  updateStoreUserRole,
  updateStoreUserDashboardAccess,
  removeStoreUser,
} from "../api/mutations";
import { useStore } from "../contexts/StoreContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotification } from "../contexts/NotificationContext";
import usePlanTier, { tierAllows } from "../hooks/usePlanTier";
import { getApiErrorMessages } from "../utils/apiErrorReasons";
import { Skeleton } from "./dashboard/components/ui";
import { ErrorMessages } from "../components/ui/Modal";
import { buttonClass } from "../components/ui/styles";
import MembersTable from "./team/MembersTable";
import InviteDialog from "./team/InviteDialog";
import RemoveMemberDialog from "./team/RemoveMemberDialog";
import RolesGuide from "./team/RolesGuide";
import { displayName } from "./team/format";

const TOAST_MS = 4000;

export default function Team() {
  const { selectedStoreId, selectedStore, canManageStore } = useStore();
  const { user } = useAuth();
  const { t } = useLanguage();
  const notifications = useNotification();
  const queryClient = useQueryClient();
  const tier = usePlanTier(selectedStore);
  // Starter stores get no scan counts at all — no column, no lock, no upsell
  // (Serhat, 2026-10-24). The API sends null there and skips the query.
  const showScans = tierAllows(tier, "insights");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [actionError, setActionError] = useState(null);

  const queryKey = ["store-users", selectedStoreId];
  const {
    data: storeUsers = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => fetchStoreUsers(selectedStoreId),
    enabled: !!selectedStoreId,
  });

  const email = user?.email?.toLowerCase();
  const isSelf = (member) =>
    !!email && member.user?.email?.toLowerCase() === email;

  const toast = (title) =>
    notifications?.showNotification(title, "", "success", [], TOAST_MS);

  // Returned, so each call stays pending until the list has refetched and the
  // row shows its new state.
  const onSettled = () => queryClient.invalidateQueries({ queryKey });
  // Shared by the role, access and remove mutations so the rows in flight can
  // be read across all of them (below), not just from each one's latest call.
  const mutationKey = ["store-users", selectedStoreId, "change"];
  const onError = (err, { member }) =>
    setActionError({
      name: displayName(member),
      messages: getApiErrorMessages(err, t, "team.errors.generic"),
    });

  const roleMutation = useMutation({
    mutationKey,
    mutationFn: ({ member, role }) =>
      updateStoreUserRole({
        storeId: selectedStoreId,
        storeUserId: member.id,
        role,
      }),
    onSuccess: (_data, { member, role }) =>
      toast(
        t(role === "admin" ? "team.toast.nowAdmin" : "team.toast.nowMember", {
          name: displayName(member),
        }),
      ),
    onError,
    onSettled,
  });

  const accessMutation = useMutation({
    mutationKey,
    mutationFn: ({ member, dashboardAccess }) =>
      updateStoreUserDashboardAccess({
        storeId: selectedStoreId,
        storeUserId: member.id,
        dashboardAccess,
      }),
    onSuccess: (_data, { member, dashboardAccess }) =>
      toast(
        t(dashboardAccess ? "team.toast.dashboard" : "team.toast.scannerOnly", {
          name: displayName(member),
        }),
      ),
    onError,
    onSettled,
  });

  const removeMutation = useMutation({
    mutationKey,
    mutationFn: ({ member }) =>
      removeStoreUser({ storeId: selectedStoreId, storeUserId: member.id }),
    onSuccess: (_data, { member }) => {
      setRemoving(null);
      toast(t("team.toast.removed", { name: displayName(member) }));
    },
    onSettled,
  });

  // Every row with a change in flight stays disabled. A useMutation's own
  // isPending/variables only describe its latest call, so starting a change on
  // row B used to re-enable row A while A's request was still running.
  const pendingRows = useMutationState({
    filters: { mutationKey, status: "pending" },
    select: (mutation) => mutation.state.variables?.member?.id,
  });
  const pendingIds = new Set(pendingRows);

  const toggleRole = (member) => {
    setActionError(null);
    roleMutation.mutate({
      member,
      role: member.role === "admin" ? "member" : "admin",
    });
  };

  const toggleAccess = (member) => {
    setActionError(null);
    accessMutation.mutate({
      member,
      dashboardAccess: member.dashboard_access === false,
    });
  };

  const askRemove = (member) => {
    setActionError(null);
    removeMutation.reset();
    setRemoving(member);
  };

  const scannerOnlyCount = storeUsers.filter(
    (m) => m.role === "member" && m.dashboard_access === false,
  ).length;

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
            {t("team.title")}
          </h1>
          {!isLoading && !error && (
            <p className="text-[15px] text-gray-700">
              {t("team.headline.people", { count: storeUsers.length })}
              {scannerOnlyCount > 0 &&
                ` · ${t("team.headline.scannerOnly", { count: scannerOnlyCount })}`}
            </p>
          )}
        </div>
        {canManageStore && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className={buttonClass.primary}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("team.invite.button")}
          </button>
        )}
      </header>

      {isLoading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[68px] w-full" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-3 rounded-[14px] border border-gray-200 bg-white p-6"
        >
          <p className="text-sm text-gray-700">{t("team.error")}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className={buttonClass.secondary}
          >
            {t("dashboard.error.retry")}
          </button>
        </div>
      ) : (
        <>
          {actionError && (
            <div className="flex flex-col gap-2">
              <ErrorMessages
                messages={[
                  t("team.errors.update", { name: actionError.name }),
                  ...actionError.messages,
                ]}
              />
            </div>
          )}
          <MembersTable
            members={storeUsers}
            isSelf={isSelf}
            showScans={showScans}
            showActions={canManageStore}
            pendingIds={pendingIds}
            onToggleRole={toggleRole}
            onToggleAccess={toggleAccess}
            onRemove={askRemove}
          />
        </>
      )}

      <RolesGuide />

      {canManageStore && (
        <InviteDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          storeId={selectedStoreId}
          onInvited={(invited) =>
            toast(t("team.toast.invited", { email: invited }))
          }
        />
      )}
      <RemoveMemberDialog
        member={removing}
        storeName={selectedStore?.name}
        isPending={removeMutation.isPending}
        errors={
          removeMutation.isError
            ? getApiErrorMessages(
                removeMutation.error,
                t,
                "team.errors.generic",
              )
            : null
        }
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing && removeMutation.mutate({ member: removing })
        }
      />
    </div>
  );
}
